import { useState, useEffect } from 'react';
import { KeyIcon } from '@heroicons/react/24/outline';
import { httpClient } from '../../api/httpClient';
import { useCacheStore } from '../../stores/cacheStore';
import SidePanel from '../ui/SidePanel';

interface KeyDetail {
  key: string;
  type?: string;
  ttl?: number;
  size?: number;
  value: any;
  encoding?: string | null;
  createdAt?: number;
  lastAccessed?: number;
}

interface KeyDetailPanelProps {
  keyName: string | null;
  onClose: () => void;
}

export function KeyDetailPanel({ keyName, onClose }: KeyDetailPanelProps) {
  const { selectedProvider } = useCacheStore();
  const [detail, setDetail] = useState<KeyDetail | null>(null);

  useEffect(() => {
    if (keyName) {
      loadKeyDetail(keyName);
    } else {
      setDetail(null);
    }
  }, [keyName, selectedProvider]);

  const loadKeyDetail = async (key: string) => {
    const response = await httpClient.get<{
      key: string;
      value: any;
      encoding: string | null;
      type?: string;
      ttl?: number;
    }>(`/api/cache/value`, { 
      params: {
        key,
        provider: selectedProvider || ''
      }
    });

    if (response.success && response.data) {
      setDetail({
        key: response.data.key,
        value: response.data.value,
        encoding: response.data.encoding,
        type: response.data.type || 'unknown',
        ttl: response.data.ttl,
      });
    }
  };

  return (
    <SidePanel
      isOpen={!!keyName}
      onClose={onClose}
      title="缓存详情"
      icon={<KeyIcon className="h-5 w-5 text-sky-400" />}
    >
      {detail ? <KeyContent detail={detail} /> : (
        <div className="flex flex-col items-center justify-center h-full text-zinc-500">
          <KeyIcon className="h-12 w-12 mb-4 opacity-20" />
          <p>请选择一个缓存键以查看详情</p>
        </div>
      )}
    </SidePanel>
  );
}

function KeyContent({ detail }: {
  detail: KeyDetail | null
}) {
  const formatTime = (timestamp?: number) => {
    if (!timestamp) return '未知';
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  const formatTTL = (seconds?: number) => {
    if (seconds === undefined || seconds === null) return '未知';
    if (seconds < 0) return '永不过期';
    if (seconds < 60) return `${seconds}秒`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}小时`;
    return `${Math.floor(seconds / 86400)}天`;
  };

  const formatSize = (bytes?: number) => {
    if (bytes === undefined || bytes === null) return '未知';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-gray-500">
        <KeyIcon className="h-12 w-12 mb-4 opacity-20" />
        <p>选择一个键以查看详情</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 mb-4">基本信息</h3>
        <dl className="space-y-4">
          <div className="flex flex-col gap-y-1">
            <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Key</dt>
            <dd className="text-sm font-medium text-zinc-200 font-mono break-all">{detail.key}</dd>
          </div>

          <div className="flex flex-col gap-y-1">
            <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">类型</dt>
            <dd className="text-sm font-medium text-zinc-200">{detail.type}</dd>
          </div>

          <div className="flex flex-col gap-y-1">
            <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">TTL</dt>
            <dd className="text-sm font-medium text-zinc-200">{formatTTL(detail.ttl)}</dd>
          </div>

          <div className="flex flex-col gap-y-1">
            <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">大小</dt>
            <dd className="text-sm font-medium text-zinc-200">{formatSize(detail.size)}</dd>
          </div>

          {detail.encoding && (
            <div className="flex flex-col gap-y-1">
              <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">编码</dt>
              <dd className="text-sm font-medium text-zinc-200">{detail.encoding}</dd>
            </div>
          )}
        </dl>
      </div>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 mb-4">数据值</h3>
        <div className="rounded-xl bg-black/50 p-4">
          <pre className="font-mono text-xs leading-relaxed text-zinc-400 overflow-x-hidden whitespace-pre-wrap break-all">
            {typeof detail.value === 'object' ? JSON.stringify(detail.value, null, 2) : String(detail.value)}
          </pre>
        </div>
      </div>

      {(detail.createdAt || detail.lastAccessed) && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 mb-4">时间统计</h3>
          <dl className="space-y-4">
            {detail.createdAt && (
              <div className="flex flex-col gap-y-1">
                <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">创建于</dt>
                <dd className="text-sm font-medium text-zinc-200 font-mono">{formatTime(detail.createdAt)}</dd>
              </div>
            )}
            {detail.lastAccessed && (
              <div className="flex flex-col gap-y-1">
                <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">最后访问</dt>
                <dd className="text-sm font-medium text-zinc-200 font-mono">{formatTime(detail.lastAccessed)}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}